import type { Segment, SegmentWithCount, WarmupStep } from '../types';

/**
 * UI helpers for segment counts and capacity previews (v5 stepped warm-up).
 */

export function capAtFromSchedule(dol: number, warmupSteps: WarmupStep[]): number {
  if (dol <= 0 || warmupSteps.length === 0) return 0;
  for (const s of warmupSteps) {
    if (dol >= s.dayFrom && dol <= s.dayTo) return s.emails;
  }
  return warmupSteps[warmupSteps.length - 1].emails;
}

export function fleetCapFromSchedule(
  day: number,
  starts: number[],
  warmupSteps: WarmupStep[],
): number {
  return starts.reduce((s, sd) => s + capAtFromSchedule(day - sd + 1, warmupSteps), 0);
}

export function dailyOnboardBatch(total: number, ramp: number): number {
  return Math.ceil(total / ramp);
}

export function activePoolAt(
  day: number,
  total: number,
  monthly: number,
  ramp: number,
  newStart: number,
): number {
  const histLoaded =
    day <= ramp ? Math.min(dailyOnboardBatch(total, ramp) * day, total) : total;
  const newLoaded = day >= newStart ? Math.round(monthly * (day - newStart) / 30) : 0;
  return Math.min(histLoaded + newLoaded, total * 6);
}

export function followUpDemand(
  day: number,
  total: number,
  monthly: number,
  ramp: number,
  newStart: number,
  freqS: number,
): number {
  if (day <= 1) return 0;
  const poolYesterday = activePoolAt(day - 1, total, monthly, ramp, newStart);
  return Math.ceil(poolYesterday / freqS);
}

export function totalEmailDemand(
  day: number,
  total: number,
  monthly: number,
  ramp: number,
  newStart: number,
  freqS: number,
): number {
  const onboard =
    day <= ramp ? dailyOnboardBatch(total, ramp) : day >= newStart ? Math.ceil(monthly / 30) : 0;
  return onboard + followUpDemand(day, total, monthly, ramp, newStart, freqS);
}

/** @deprecated Use capAtFromSchedule */
export function capAt(dol: number, cap: number, ramp: number): number {
  if (dol <= 0) return 0;
  if (dol >= ramp) return cap;
  return Math.round(20 + (cap - 20) * (dol / ramp));
}

/** @deprecated Use fleetCapFromSchedule */
export function fleetCap(
  day: number,
  starts: number[],
  cap: number,
  ramp: number,
): number {
  return starts.reduce((s, sd) => s + capAt(day - sd + 1, cap, ramp), 0);
}

/** @deprecated Use activePoolAt */
export function activeAt(
  day: number,
  total: number,
  monthly: number,
  ramp: number,
  newStart: number,
): number {
  return activePoolAt(day, total, monthly, ramp, newStart);
}

export function segsWithCounts(segs: Segment[], total: number): SegmentWithCount[] {
  return segs.map((s) => ({
    ...s,
    count: Math.max(1, Math.round((s.pct / 100) * total)),
  }));
}
